import React from "react";
import { Link, useHistory } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import { toast } from "react-toastify";
import { lang } from "../../../middleware/lang";
import {
  GET_PUBLISH_STATUS,
  PUBLISH_PARTNER,
  PublishStatusData,
} from "./queries";
import {
  CREATE_CONNECT_ACCOUNT,
  CreateConnectAccountResponse,
} from "../payments/queries";
import { LoaderDots } from "../../../middleware/loaders";

export type PublishRequirement = {
  hasProducts: boolean;
  hasBranch: boolean;
  hasLogo: boolean;
  hasPageUrl: boolean;
  hasBankAccount: boolean;
};

export function computePublishRequirements(
  data: PublishStatusData,
): PublishRequirement {
  const account = data.getConnectAccount;
  return {
    hasBranch: data.getBranches.length > 0,
    hasProducts: data.getProductCount > 0,
    hasLogo: data.getPartner.logo !== null,
    hasPageUrl:
      data.getPartner.url_name !== null &&
      data.getPartner.url_name.length >= 3,
    hasBankAccount:
      account !== null &&
      account.charges_enabled &&
      account.payouts_enabled,
  };
}

type StepDef = {
  key: keyof PublishRequirement;
  title: string;
  description: string;
  icon: string;
  actionLabel: string;
  actionLink?: string;
};

const STEPS: StepDef[] = [
  {
    key: "hasProducts",
    title: lang("Productos", "Products"),
    description: lang(
      "Agrega al menos un producto a tu menú",
      "Add at least one product to your menu",
    ),
    icon: "fa-utensils",
    actionLabel: lang("Agregar producto", "Add product"),
    actionLink: "/products/new",
  },
  {
    key: "hasBranch",
    title: lang("Sucursales", "Branches"),
    description: lang(
      "Tu restaurante necesita al menos una sucursal con dirección",
      "Your restaurant needs at least one branch with an address",
    ),
    icon: "fa-map-marker-alt",
    actionLabel: lang("Agregar sucursal", "Add branch"),
    actionLink: "/branches/create",
  },
  {
    key: "hasLogo",
    title: lang("Logo", "Logo"),
    description: lang(
      "Sube el logo de tu restaurante para que tus clientes te identifiquen",
      "Upload your restaurant logo so customers can recognize you",
    ),
    icon: "fa-image",
    actionLabel: lang("Subir logo", "Upload logo"),
    actionLink: "/account",
  },
  {
    key: "hasPageUrl",
    title: lang("URL de tu página", "Page URL"),
    description: lang(
      "Configura la dirección web de tu restaurante en la app",
      "Set up your restaurant's web address in the app",
    ),
    icon: "fa-link",
    actionLabel: lang("Configurar URL", "Set up URL"),
    actionLink: "/account",
  },
  {
    key: "hasBankAccount",
    title: lang("Cuenta bancaria", "Bank account"),
    description: lang(
      "Conecta tu cuenta bancaria para recibir pagos de tus clientes",
      "Connect your bank account to receive payments from your customers",
    ),
    icon: "fa-university",
    actionLabel: lang("Conectar cuenta", "Connect account"),
  },
];

const PublishPage: React.FC = () => {
  const { data, loading, refetch } = useQuery<PublishStatusData>(
    GET_PUBLISH_STATUS,
    { fetchPolicy: "network-only" },
  );

  const [publishPartner, { loading: publishing }] = useMutation(
    PUBLISH_PARTNER,
    {
      onCompleted: () => {
        toast.success(
          lang(
            "¡Tu restaurante está publicado!",
            "Your restaurant is published!",
          ),
        );
      },
      onError: (err) => {
        toast.error(err.message);
      },
    },
  );

  if (loading && !data) {
    return <LoaderDots />;
  }

  if (!data) {
    return null;
  }

  const isAlreadyPublished = data.getPartner.status === "1";
  const requirements = computePublishRequirements(data);
  const completedCount = STEPS.filter((s) => requirements[s.key]).length;
  const allComplete = STEPS.every((s) => requirements[s.key]);

  const handlePublish = async () => {
    try {
      await publishPartner();
      refetch();
    } catch {
      // error handled by onError
    }
  };

  return (
    <main>
      <div className="container" style={{ maxWidth: 700, paddingTop: "2rem" }}>
        <div className="text-center mb-4">
          <h2 style={{ fontWeight: 700 }}>
            {isAlreadyPublished
              ? lang("Tu restaurante está en línea", "Your restaurant is live")
              : lang("Publicar tu restaurante", "Publish your restaurant")}
          </h2>
          {!isAlreadyPublished && (
            <p className="text-muted">
              {lang(
                "Completa estos requisitos para que tu restaurante aparezca en la app",
                "Complete these requirements so your restaurant appears in the app",
              )}
            </p>
          )}
        </div>

        {/* Progress bar */}
        <div style={{ marginBottom: "2rem" }}>
          <div className="d-flex justify-content-between mb-1">
            <small className="text-muted">
              {lang("Requisitos", "Requirements")}
            </small>
            <small className="text-muted">
              {completedCount} / {STEPS.length}
            </small>
          </div>
          <div className="progress" style={{ height: 8, borderRadius: 4 }}>
            <div
              className="progress-bar"
              style={{
                width: `${(completedCount / STEPS.length) * 100}%`,
                backgroundColor:
                  allComplete || isAlreadyPublished ? "#48BB78" : "#FFBB28",
                transition: "width 0.3s",
              }}
            />
          </div>
        </div>

        {isAlreadyPublished ? (
          <PublishedState />
        ) : (
          <>
            {STEPS.map((step) => {
              const isComplete = requirements[step.key];
              return (
                <StepCard
                  key={step.key}
                  step={step}
                  isComplete={isComplete}
                  onRefresh={refetch}
                />
              );
            })}

            {/* Publish button */}
            <div className="text-center mt-4 mb-4">
              {allComplete ? (
                <button
                  className="btn btn-lg"
                  style={{
                    background: "#48BB78",
                    color: "#fff",
                    fontWeight: 700,
                    borderRadius: 8,
                    padding: "0.75rem 2.5rem",
                    fontSize: 18,
                  }}
                  onClick={handlePublish}
                  disabled={publishing}
                >
                  {publishing ? (
                    <span className="spinner-border spinner-border-sm mr-2" />
                  ) : (
                    <i className="fas fa-rocket mr-2" />
                  )}
                  {lang("Publicar restaurante", "Publish restaurant")}
                </button>
              ) : (
                <div
                  className="alert"
                  style={{
                    background: "#FFF9E6",
                    border: "1px solid #FFBB28",
                    borderRadius: 8,
                  }}
                >
                  <i
                    className="fas fa-info-circle mr-2"
                    style={{ color: "#FFBB28" }}
                  />
                  {lang(
                    "Completa todos los requisitos para poder publicar tu restaurante",
                    "Complete all requirements to publish your restaurant",
                  )}
                </div>
              )}

              <button
                className="btn btn-outline-secondary btn-sm mt-3"
                onClick={() => refetch()}
              >
                <i className="fas fa-sync-alt mr-1" />
                {lang("Verificar requisitos", "Check requirements")}
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

const PublishedState: React.FC = () => {
  const history = useHistory();
  return (
    <div className="text-center">
      <div
        style={{
          width: 80,
          height: 80,
          borderRadius: "50%",
          background: "#48BB78",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 1.5rem",
        }}
      >
        <i className="fas fa-check" style={{ fontSize: 36, color: "#fff" }} />
      </div>
      <p style={{ fontSize: 18, marginBottom: "2rem" }}>
        {lang(
          "Tu restaurante está visible en la app y puede recibir pedidos.",
          "Your restaurant is visible in the app and can receive orders.",
        )}
      </p>
      <button
        className="btn btn-lg"
        style={{
          background: "#FFBB28",
          color: "#292929",
          fontWeight: 700,
          borderRadius: 8,
          padding: "0.75rem 2rem",
        }}
        onClick={() => history.push("/")}
      >
        {lang("Ir al dashboard", "Go to dashboard")}
      </button>
    </div>
  );
};

type StepCardProps = {
  step: StepDef;
  isComplete: boolean;
  onRefresh: () => void;
};

const StepCard: React.FC<StepCardProps> = ({ step, isComplete, onRefresh }) => {
  const borderColor = isComplete ? "#48BB78" : "#e0e0e0";

  return (
    <div
      className="card mb-3"
      style={{
        borderLeft: `4px solid ${borderColor}`,
      }}
    >
      <div className="card-body">
        <div className="d-flex align-items-center">
          {/* Status icon */}
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: isComplete ? "#48BB78" : "#e0e0e0",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginRight: 16,
              flexShrink: 0,
            }}
          >
            {isComplete ? (
              <i className="fas fa-check" style={{ color: "#fff" }} />
            ) : (
              <i
                className={`fas ${step.icon}`}
                style={{ color: "#666", fontSize: 14 }}
              />
            )}
          </div>

          <div style={{ flex: 1 }}>
            <h5 style={{ margin: 0, fontWeight: 600 }}>
              {step.title}
            </h5>
            <p className="text-muted mb-0" style={{ fontSize: 14 }}>
              {step.description}
            </p>
          </div>

          {isComplete ? (
            <span className="badge badge-success" style={{ fontSize: 12 }}>
              <i className="fas fa-check mr-1" />
              {lang("Listo", "Done")}
            </span>
          ) : (
            <StepAction step={step} onRefresh={onRefresh} />
          )}
        </div>
      </div>
    </div>
  );
};

type StepActionProps = {
  step: StepDef;
  onRefresh: () => void;
};

const StepAction: React.FC<StepActionProps> = ({ step, onRefresh }) => {
  if (step.key === "hasBankAccount") {
    return <BankAccountAction />;
  }

  if (step.actionLink) {
    return (
      <Link
        to={step.actionLink}
        className="btn btn-sm"
        style={{
          background: "#FFBB28",
          color: "#292929",
          fontWeight: 600,
          borderRadius: 6,
          whiteSpace: "nowrap",
        }}
      >
        {step.actionLabel}
      </Link>
    );
  }

  return null;
};

const BankAccountAction: React.FC = () => {
  const [createAccount, { loading }] =
    useMutation<CreateConnectAccountResponse>(CREATE_CONNECT_ACCOUNT);

  const handleConnect = async () => {
    try {
      const { data } = await createAccount();
      if (data?.createConnectAccount.url) {
        window.location.href = data.createConnectAccount.url;
      }
    } catch (e: any) {
      toast.error(
        e.message ||
          lang("Error al conectar cuenta", "Error connecting account"),
      );
    }
  };

  return (
    <button
      className="btn btn-sm"
      style={{
        background: "#FFBB28",
        color: "#292929",
        fontWeight: 600,
        borderRadius: 6,
        whiteSpace: "nowrap",
      }}
      onClick={handleConnect}
      disabled={loading}
    >
      {loading ? (
        <span className="spinner-border spinner-border-sm mr-1" />
      ) : (
        <i className="fas fa-link mr-1" />
      )}
      {lang("Conectar cuenta", "Connect account")}
    </button>
  );
};

export default PublishPage;
